<template>
  <AppLayout>
    <div class="bureau-page">
      <div class="page-header">
        <h1>Bureau</h1>
        <div class="page-actions">
          <BaseButton variant="primary" @click="showCreateModal = true">
            + Nouveau projet
          </BaseButton>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>

      <section class="panel-section">
        <div class="section-header">
          <h2>Projets</h2>
        </div>
        <BaseTable
          :columns="projetColumns"
          :items="projets"
          :loading="loading"
          :show-actions="true"
          @view="viewProjet"
        >
          <template #cell-statut="{ value }">
            <BaseBadge :variant="getStatutVariant(value)">{{ value }}</BaseBadge>
          </template>
          <template #cell-typeProjet="{ value }">
            {{ value === 'CLE_EN_MAIN' ? 'Clé en main' : 'Catalogue' }}
          </template>
          <template #cell-dateDebut="{ value }">
            {{ value ? formatDate(value) : '-' }}
          </template>
          <template #cell-dateFin="{ value }">
            {{ value ? formatDate(value) : '-' }}
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="viewProjet(item)">
              Voir
            </BaseButton>
            <BaseButton variant="ghost" size="sm" class="btn-danger" @click="handleDeleteProjet((item as Projet).id, (item as Projet).nom)" title="Supprimer le projet" :disabled="deletingProjetId !== null">
              Supprimer
            </BaseButton>
          </template>
        </BaseTable>
      </section>

      <!-- Section À réconcilier -->
      <section class="panel-section" v-if="reconciliation.total > 0 || loadingReconciliation">
        <div class="section-header">
          <h2>
            À réconcilier
            <span v-if="reconciliation.total > 0" class="reconciliation-badge">{{ reconciliation.total }}</span>
          </h2>
          <BaseButton variant="ghost" size="sm" @click="loadReconciliation">Rafraîchir</BaseButton>
        </div>

        <div v-if="loadingReconciliation" class="loading-text">Chargement...</div>
        <div v-if="reconciliationError" class="error" style="margin-bottom:8px;">{{ reconciliationError }}</div>

        <!-- Commandes auto -->
        <div v-if="reconciliation.itemsCommande.length > 0">
          <h3 class="rec-subtitle">Commandes auto-générées ({{ reconciliation.itemsCommande.length }})</h3>
          <BaseTable :columns="commandeAutoColumns" :items="reconciliation.itemsCommande" :show-actions="true">
            <template #cell-creeLe="{ value }">{{ formatDate(value as string) }}</template>
            <template #actions="{ item }">
              <BaseButton variant="ghost" size="sm" @click="validerRecord('commande', (item as any).id)">Valider</BaseButton>
              <BaseButton variant="ghost" size="sm" class="btn-danger" @click="supprimerRecord('commande', (item as any).id)">Supprimer</BaseButton>
            </template>
          </BaseTable>
        </div>

        <!-- Expéditions auto -->
        <div v-if="reconciliation.expeditions.length > 0">
          <h3 class="rec-subtitle">Expéditions auto-générées ({{ reconciliation.expeditions.length }})</h3>
          <BaseTable :columns="expeditionAutoColumns" :items="reconciliation.expeditions" :show-actions="true">
            <template #cell-dateExpedition="{ value }">{{ formatDate(value as string) }}</template>
            <template #cell-creeLe="{ value }">{{ formatDate(value as string) }}</template>
            <template #actions="{ item }">
              <BaseButton variant="ghost" size="sm" @click="validerRecord('expedition', (item as any).id)">Valider</BaseButton>
              <BaseButton variant="ghost" size="sm" class="btn-danger" @click="supprimerRecord('expedition', (item as any).id)">Supprimer</BaseButton>
            </template>
          </BaseTable>
        </div>

        <!-- Lots auto -->
        <div v-if="reconciliation.lots.length > 0">
          <h3 class="rec-subtitle">Lots de production auto-générés ({{ reconciliation.lots.length }})</h3>
          <BaseTable :columns="lotAutoColumns" :items="reconciliation.lots" :show-actions="true">
            <template #cell-dateProduction="{ value }">{{ value ? formatDate(value as string) : '-' }}</template>
            <template #cell-creeLe="{ value }">{{ formatDate(value as string) }}</template>
            <template #actions="{ item }">
              <BaseButton variant="ghost" size="sm" @click="validerRecord('lot', (item as any).id)">Valider</BaseButton>
              <BaseButton variant="ghost" size="sm" class="btn-danger" @click="supprimerRecord('lot', (item as any).id)">Supprimer</BaseButton>
            </template>
          </BaseTable>
        </div>
      </section>
    </div>

    <!-- Modal de création/édition de projet -->
    <FormPage v-if="showCreateModal || showEditModal" @close="closeModal">
      <template #title>
        {{ showCreateModal ? 'Nouveau projet' : 'Modifier projet' }}
      </template>
      <form @submit.prevent="handleSubmit" class="projet-form">
        <BaseInput
          v-model.trim="projetForm.nom"
          label="Nom"
          placeholder="Nom du projet"
          required
        />
        <BaseInput
          v-model.trim="projetForm.code"
          label="Code"
          placeholder="Code du projet"
          required
        />
        <BaseInput
          v-model.trim="projetForm.localisation"
          label="Localisation"
          placeholder="Localisation"
        />
        <BaseTextarea
          v-model.trim="projetForm.description"
          label="Description"
          placeholder="Description du projet"
        />
        <BaseSelect
          v-model="projetForm.statut"
          label="Statut"
          required
        >
          <option value="PLANIFIE">Planifié</option>
          <option value="EN_COURS">En cours</option>
          <option value="TERMINE">Terminé</option>
        </BaseSelect>
        <BaseSelect
          v-model="projetForm.typeProjet"
          label="Type de projet"
          required
        >
          <option value="CLE_EN_MAIN">Clé en main</option>
          <option value="CATALOGUE">Catalogue</option>
        </BaseSelect>

        <!-- Section Fichiers -->
        <div class="files-section">
          <h3 class="files-section-title">Fichiers associés</h3>
          <div class="files-grid">
            <div class="file-input-group">
              <label class="file-label">Bon de commande / Marché</label>
              <input
                type="file"
                :ref="el => fileInputRefs['bon-de-commande'] = el as HTMLInputElement"
                @change="handleFileSelect('bon-de-commande', $event)"
                class="file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
              <div v-if="selectedFiles['bon-de-commande']" class="selected-file">
                <span>{{ selectedFiles['bon-de-commande'].name }}</span>
                <button type="button" @click="removeFile('bon-de-commande')" class="remove-file-btn">×</button>
              </div>
              <div v-if="showEditModal && currentFiles['bon-de-commande']" class="current-file">
                <span>Fichier actuel: {{ currentFiles['bon-de-commande'] }}</span>
              </div>
            </div>

            <div class="file-input-group">
              <label class="file-label">Reçu versement paiement</label>
              <input
                type="file"
                :ref="el => fileInputRefs['recu-versement-paiement'] = el as HTMLInputElement"
                @change="handleFileSelect('recu-versement-paiement', $event)"
                class="file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
              <div v-if="selectedFiles['recu-versement-paiement']" class="selected-file">
                <span>{{ selectedFiles['recu-versement-paiement'].name }}</span>
                <button type="button" @click="removeFile('recu-versement-paiement')" class="remove-file-btn">×</button>
              </div>
              <div v-if="showEditModal && currentFiles['recu-versement-paiement']" class="current-file">
                <span>Fichier actuel: {{ currentFiles['recu-versement-paiement'] }}</span>
              </div>
            </div>

            <div class="file-input-group">
              <label class="file-label">Budget prévisionnel</label>
              <input
                type="file"
                :ref="el => fileInputRefs['budget-previsionnel'] = el as HTMLInputElement"
                @change="handleFileSelect('budget-previsionnel', $event)"
                class="file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
              <div v-if="selectedFiles['budget-previsionnel']" class="selected-file">
                <span>{{ selectedFiles['budget-previsionnel'].name }}</span>
                <button type="button" @click="removeFile('budget-previsionnel')" class="remove-file-btn">×</button>
              </div>
              <div v-if="showEditModal && currentFiles['budget-previsionnel']" class="current-file">
                <span>Fichier actuel: {{ currentFiles['budget-previsionnel'] }}</span>
              </div>
            </div>

            <div class="file-input-group">
              <label class="file-label">Budget validé</label>
              <input
                type="file"
                :ref="el => fileInputRefs['budget-valide'] = el as HTMLInputElement"
                @change="handleFileSelect('budget-valide', $event)"
                class="file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
              <div v-if="selectedFiles['budget-valide']" class="selected-file">
                <span>{{ selectedFiles['budget-valide'].name }}</span>
                <button type="button" @click="removeFile('budget-valide')" class="remove-file-btn">×</button>
              </div>
              <div v-if="showEditModal && currentFiles['budget-valide']" class="current-file">
                <span>Fichier actuel: {{ currentFiles['budget-valide'] }}</span>
              </div>
            </div>

            <div class="file-input-group">
              <label class="file-label">Demande d'achat</label>
              <input
                type="file"
                :ref="el => fileInputRefs['demande-achat'] = el as HTMLInputElement"
                @change="handleFileSelect('demande-achat', $event)"
                class="file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
              <div v-if="selectedFiles['demande-achat']" class="selected-file">
                <span>{{ selectedFiles['demande-achat'].name }}</span>
                <button type="button" @click="removeFile('demande-achat')" class="remove-file-btn">×</button>
              </div>
              <div v-if="showEditModal && currentFiles['demande-achat']" class="current-file">
                <span>Fichier actuel: {{ currentFiles['demande-achat'] }}</span>
              </div>
            </div>

            <div class="file-input-group">
              <label class="file-label">Achats valorisés</label>
              <input
                type="file"
                :ref="el => fileInputRefs['achats-valorises'] = el as HTMLInputElement"
                @change="handleFileSelect('achats-valorises', $event)"
                class="file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
              <div v-if="selectedFiles['achats-valorises']" class="selected-file">
                <span>{{ selectedFiles['achats-valorises'].name }}</span>
                <button type="button" @click="removeFile('achats-valorises')" class="remove-file-btn">×</button>
              </div>
              <div v-if="showEditModal && currentFiles['achats-valorises']" class="current-file">
                <span>Fichier actuel: {{ currentFiles['achats-valorises'] }}</span>
              </div>
            </div>

            <div class="file-input-group">
              <label class="file-label">Bon de commande fournisseur</label>
              <input
                type="file"
                :ref="el => fileInputRefs['bon-commande-fournisseur'] = el as HTMLInputElement"
                @change="handleFileSelect('bon-commande-fournisseur', $event)"
                class="file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
              />
              <div v-if="selectedFiles['bon-commande-fournisseur']" class="selected-file">
                <span>{{ selectedFiles['bon-commande-fournisseur'].name }}</span>
                <button type="button" @click="removeFile('bon-commande-fournisseur')" class="remove-file-btn">×</button>
              </div>
              <div v-if="showEditModal && currentFiles['bon-commande-fournisseur']" class="current-file">
                <span>Fichier actuel: {{ currentFiles['bon-commande-fournisseur'] }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="formError" class="error-message">{{ formError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingProjet">
            {{ showCreateModal ? 'Créer' : 'Enregistrer' }}
          </BaseButton>
        </div>
      </form>
    </FormPage>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseCard from '../components/BaseCard.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseTextarea from '../components/BaseTextarea.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseBadge from '../components/BaseBadge.vue';
import BaseTable, { type TableColumn } from '../components/BaseTable.vue';
import FormPage from '../components/FormPage.vue';
import { apiFetch } from '../utils/api';

interface Projet {
  id: number;
  nom: string;
  code: string;
  localisation: string | null;
  description: string | null;
  statut: string;
  dateDebut: string | null;
  dateFin: string | null;
  typeProjet?: 'CATALOGUE' | 'CLE_EN_MAIN';
  fichierBonDeCommande?: string | null;
  fichierRecuVersementPaiement?: string | null;
  fichierBudgetPrevisionnel?: string | null;
  fichierBudgetValide?: string | null;
  fichierDemandeAchat?: string | null;
  fichierAchatsValorises?: string | null;
  fichierBonCommandeFournisseur?: string | null;
  creeLe: string;
}

const router = useRouter();
const projets = ref<Projet[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const showCreateModal = ref(false);
const showEditModal = ref(false);
const savingProjet = ref(false);
const formError = ref<string | null>(null);
const editingProjetId = ref<number | null>(null);
const deletingProjetId = ref<number | null>(null);

// Gestion des fichiers
const selectedFiles = ref<Record<string, File>>({});
const currentFiles = ref<Record<string, string>>({});
const fileInputRefs = ref<Record<string, HTMLInputElement | null>>({});

const projetForm = reactive({
  nom: '',
  code: '',
  localisation: '',
  description: '',
  statut: 'PLANIFIE',
  typeProjet: 'CLE_EN_MAIN' as 'CATALOGUE' | 'CLE_EN_MAIN'
});

const projetColumns: TableColumn[] = [
  { key: 'nom', label: 'Nom' },
  { key: 'code', label: 'Code' },
  { key: 'localisation', label: 'Localisation' },
  { key: 'typeProjet', label: 'Type' },
  { key: 'statut', label: 'Statut' },
  { key: 'dateDebut', label: 'Date début' },
  { key: 'dateFin', label: 'Date fin' }
];


// ── Réconciliation ──
const loadingReconciliation = ref(false);
const reconciliationError = ref<string | null>(null);
const reconciliation = ref<{
  itemsCommande: any[];
  expeditions: any[];
  lots: any[];
  total: number;
}>({ itemsCommande: [], expeditions: [], lots: [], total: 0 });

const commandeAutoColumns: TableColumn[] = [
  { key: 'projetNom', label: 'Projet' },
  { key: 'siteNom', label: 'Site' },
  { key: 'typeLibelle', label: 'Type élément' },
  { key: 'quantiteCommande', label: 'Qté commandée' },
  { key: 'creeLe', label: 'Créé le' }
];
const expeditionAutoColumns: TableColumn[] = [
  { key: 'projetNom', label: 'Projet' },
  { key: 'siteNom', label: 'Site' },
  { key: 'dateExpedition', label: 'Date expédition' },
  { key: 'statut', label: 'Statut' },
  { key: 'creeLe', label: 'Créé le' }
];
const lotAutoColumns: TableColumn[] = [
  { key: 'projetNom', label: 'Projet' },
  { key: 'typeLibelle', label: 'Type élément' },
  { key: 'quantiteProduite', label: 'Qté produite' },
  { key: 'dateProduction', label: 'Date prod.' },
  { key: 'creeLe', label: 'Créé le' }
];

async function loadReconciliation() {
  loadingReconciliation.value = true;
  try {
    const res = await apiFetch('/reconciliation') as typeof reconciliation.value;
    reconciliation.value = res;
  } catch { /* silencieux */ }
  finally { loadingReconciliation.value = false; }
}

async function validerRecord(type: string, id: number) {
  reconciliationError.value = null;
  try {
    await apiFetch(`/reconciliation/${type}/${id}/valider`, { method: 'PATCH' });
    loadReconciliation();
  } catch (e: any) {
    reconciliationError.value = e.message || 'Erreur lors de la validation';
  }
}

async function supprimerRecord(type: string, id: number) {
  if (!confirm('Supprimer ce record auto-généré ?')) return;
  reconciliationError.value = null;
  try {
    await apiFetch(`/reconciliation/${type}/${id}`, { method: 'DELETE' });
    loadReconciliation();
  } catch (e: any) {
    reconciliationError.value = e.message || 'Erreur lors de la suppression';
  }
}

onMounted(() => {
  loadProjets();
  loadReconciliation();
});

async function loadProjets() {
  loading.value = true;
  error.value = null;
  try {
    const response = await apiFetch('/projets');
    projets.value = (response as { items: Projet[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement';
  } finally {
    loading.value = false;
  }
}

function viewProjet(projet: Projet) {
  router.push({ name: 'projet-detail', params: { id: projet.id } });
}

function closeModal() {
  showCreateModal.value = false;
  showEditModal.value = false;
  formError.value = null;
  editingProjetId.value = null;
  projetForm.nom = '';
  projetForm.code = '';
  projetForm.localisation = '';
  projetForm.description = '';
  projetForm.statut = 'PLANIFIE';
  projetForm.typeProjet = 'CLE_EN_MAIN';
  selectedFiles.value = {};
  currentFiles.value = {};
  Object.values(fileInputRefs.value).forEach(input => {
    if (input) input.value = '';
  });
}

function handleFileSelect(typeFichier: string, event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    selectedFiles.value[typeFichier] = target.files[0];
  }
}

function removeFile(typeFichier: string) {
  delete selectedFiles.value[typeFichier];
  const input = fileInputRefs.value[typeFichier];
  if (input) {
    input.value = '';
  }
}

async function handleSubmit() {
  formError.value = null;
  savingProjet.value = true;

  try {
    const body: {
      nom: string;
      code: string;
      statut: string;
      typeProjet: 'CATALOGUE' | 'CLE_EN_MAIN';
      localisation?: string;
      description?: string;
    } = {
      nom: projetForm.nom,
      code: projetForm.code,
      statut: projetForm.statut,
      typeProjet: projetForm.typeProjet
    };
    if (projetForm.localisation) body.localisation = projetForm.localisation;
    if (projetForm.description) body.description = projetForm.description;

    if (showCreateModal.value) {
      await apiFetch('/projets', {
        method: 'POST',
        body
      });
    } else if (editingProjetId.value) {
      await apiFetch(`/projets/${editingProjetId.value}`, {
        method: 'PATCH',
        body
      });
    } else {
      return;
    }

    // Upload des fichiers sélectionnés (frontend only - pas de backend)
    for (const [typeFichier, file] of Object.entries(selectedFiles.value)) {
      console.log(`Upload fichier ${typeFichier}:`, file.name);
      // TODO: Implémenter l'upload côté backend
      // await uploadProjectFile(projectId, typeFichier, file);
    }

    closeModal();
    loadProjets();
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement';
  } finally {
    savingProjet.value = false;
  }
}

async function handleDeleteProjet(projetId: number, projetNom: string) {
  if (deletingProjetId.value !== null) return;
  if (!confirm(`Supprimer le projet "${projetNom}" ? Cette action est irréversible.`)) return;
  deletingProjetId.value = projetId;
  try {
    await apiFetch(`/projets/${projetId}`, { method: 'DELETE' });
    await loadProjets();
  } catch (err) {
    alert(err instanceof Error ? err.message : 'Erreur lors de la suppression');
  } finally {
    deletingProjetId.value = null;
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR');
}

function getStatutVariant(statut: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (statut) {
    case 'TERMINE':
      return 'success';
    case 'EN_COURS':
      return 'primary';
    case 'PLANIFIE':
      return 'default';
    case 'ANNULE':
      return 'error';
    default:
      return 'default';
  }
}
</script>

<style scoped>
.bureau-page {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
  gap: var(--spacing-md);
}

.page-header h1 {
  margin: 0;
}

.page-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.kpis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: var(--spacing-md);
}

.kpi-card {
  padding: var(--spacing-lg);
  text-align: center;
}

.kpi-value {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.kpi-label {
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.panel-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-header h2 {
  margin: 0;
  font-size: var(--font-size-2xl);
}

.projet-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.error-message {
  padding: var(--spacing-sm);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  font-size: var(--font-size-sm);
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  margin-bottom: var(--spacing-lg);
}

.files-section {
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--color-border-light);
}

.files-section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-md);
  color: var(--color-text-primary);
}

.files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-md);
}

.file-input-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.file-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-xs);
}

.file-input {
  padding: var(--spacing-sm);
  border: 2px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all var(--transition-base);
}

.file-input:hover {
  border-color: var(--color-border-focus);
}

.file-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: var(--shadow-focus);
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
}

.remove-file-btn {
  background: none;
  border: none;
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  transition: all var(--transition-base);
}

.remove-file-btn:hover {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
}

.current-file {
  padding: var(--spacing-xs);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-style: italic;
}

.btn-danger {
  color: var(--color-danger, #dc2626);
}

.reconciliation-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 700;
  margin-left: 8px;
}

.rec-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin: 16px 0 8px;
}

.loading-text {
  color: #6b7280;
  font-size: 14px;
  padding: 8px 0;
}
</style>
